// @flow

import * as React from 'react';
/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
import type { ReactComponentStyled } from 'styled-components';
import styled from 'styled-components';
import { InputOption, Legend } from '../../internal/internal-typography';
import { Grid } from '../../layouts/grid';
import Theme from '../checkbox/theme';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

type Option = {
  label: string,
  value: string | number,
};

type Props = {
  disabled?: boolean,
  legend: string,
  options: Array<Option>,
  value?: string | number,
  onChange: (
    value: string | number,
    e?: SyntheticEvent<HTMLInputElement>
  ) => void,
  ...DataProvidesProps,
};

const Radio = ({ checked, disabled, label, onChange, value }) => {
  return (
    <StyledLabel disabled={disabled}>
      <Grid container spacing={2}>
        <Grid item>
          <RadioWrapper>
            <RadioInput
              disabled={disabled}
              type="radio"
              value={value}
              checked={checked}
              onChange={onChange}
            />
            <VisualRadioButton />
          </RadioWrapper>
        </Grid>
        <Grid item xs>
          {label}
        </Grid>
      </Grid>
    </StyledLabel>
  );
};

const VisualRadioButton = () => {
  return (
    <span>
      <FilledCircle />
    </span>
  );
};

const FilledCircle = () => {
  return (
    <svg width="8" height="8" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4" cy="4" r="4" />
    </svg>
  );
};

const RadioGroup = (props: Props) => {
  const { disabled, legend, onChange, options, value } = props;

  return (
    <Fieldset {...getDataProvidesProps(props)}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Legend>{legend}</Legend>
        </Grid>
        <Grid item>
          <Grid
            direction="column"
            container
            justifyContent="flex-start"
            spacing={1}
          >
            {options.map((option) => {
              const checked = value === option.value;

              return (
                <Grid key={option.value} item>
                  <Radio
                    disabled={disabled}
                    label={option.label}
                    value={option.value}
                    checked={checked}
                    onChange={(e) => onChange(option.value, e)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </Fieldset>
  );
};
RadioGroup.displayName = 'RadioGroup';
RadioGroup.defaultProps = { 'data-provides': 'radio-group' };

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const Fieldset = styled.fieldset`
  border: none;
  color: ${Theme.default.labelColor};
  font-size: 16px;
`;

const StyledLabel: ReactComponentStyled<{}> = styled(InputOption)`
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `cursor: not-allowed;
     color: ${Theme.disabled.labelColor};`};
`;

const RadioInput = styled.input`
  margin: 0;
  height: 18px;
  width: 18px;
`;

const RadioWrapper = styled.span`
  input {
    // Hide the browser default checkbox in a purely visual way (will still be recognized by screen readers)
    appearance: none;
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  span {
    background-color: ${Theme.default.backgroundColor};
    display: inline-block;
    position: relative;
    height: 1.125rem;
    width: 1.125rem;
    margin-top: 1px;
    border: 1px solid ${Theme.default.inputColor};
    border-radius: 50%;
    box-sizing: border-box;
    top: 0.125rem;
    left: 0;

    svg {
      fill: ${Theme.default.inputColor};
      opacity: 0;
      position: absolute;
      top: 4px;
      left: 4px;
    }
  }

  input:checked + span > svg,
  input:active + span > svg {
    opacity: 1;
  }

  input:focus + span {
    box-shadow: 0px 0px 5px 0px ${Theme.default.inputColor}99;
  }

  input:hover + span {
    border-color: ${Theme.hover.inputColor};

    > svg circle {
      fill: ${Theme.hover.inputColor};
    }
  }

  input:active + span {
    border-color: ${Theme.active.inputColor};

    > svg circle {
      fill: ${Theme.active.inputColor};
    }
  }

  input:disabled + span {
    border-color: ${Theme.disabled.inputColor};
    cursor: not-allowed;

    > svg circle {
      fill: ${Theme.disabled.inputColor};
    }
  }
`;

export default RadioGroup;
